import { useState } from 'react';
import i18n from '@/i18n';

interface TextField {
  key: string;
  label: string;
  multiline?: boolean;
}

const TEXT_FIELDS: TextField[] = [
  { key: 'hero_title', label: 'Басты заголовок (Hero Title)', multiline: false },
  { key: 'hero_subtitle', label: 'Подзаголовок (Hero Subtitle)', multiline: false },
  { key: 'about_title', label: '«Бiз туралы» заголовок / О нас', multiline: false },
  { key: 'about_text', label: '«Бiз туралы» описание / О нас', multiline: true },
  { key: 'quote_title', label: 'Цитата: Заголовок', multiline: false },
  { key: 'quote_text', label: 'Цитата: Текст', multiline: true },
  { key: 'contact_phone', label: 'Байланыс телефоны / Телефон', multiline: false },
  { key: 'contact_email', label: 'Email', multiline: false },
  { key: 'contact_address', label: 'Мекенжайы / Адрес', multiline: false },
];

const DEFAULT_TEXTS: Record<'kk' | 'ru' | 'en', Record<string, string>> = {
  kk: {
    hero_title: 'ЖАУЫНГЕР ЖОЛЫ',
    hero_subtitle: '«Әскери-қолданбалы спорт түрлері бойынша Ұлттық Федерациясы» Қоғамдық Бірлестігі',
    about_title: 'Федерация туралы',
    about_text: '«Әскери-қолданбалы спорт түрлері бойынша Ұлттық Федерациясы» Қоғамдық Бірлестігі елдің үздік атлеттерін біріктіреді, дене шынықтыру мен жауынгерлік дайындықты дамытады.',
    quote_title: 'Мұнда күш, мінез бен абырой туады.',
    quote_text: 'Әскери-қолданбалы спорт түрлері — шеберлікке, жауапкершілікке және Отанға қызмет етуге апаратын жол.',
    contact_phone: '+7 701 088 8808',
    contact_email: 'info@militarysport.kz',
    contact_address: 'Алматы қ., Қазақстан Республикасы',
  },
  ru: {
    hero_title: 'ПУТЬ ВОИНА',
    hero_subtitle: 'Общественная Организация «НАЦИОНАЛЬНАЯ ФЕДЕРАЦИЯ ПО ВОЕННО – ПРИКЛАДНЫМ ВИДАМ СПОРТА»',
    about_title: 'О Федерации',
    about_text: 'Общественная Организация «НАЦИОНАЛЬНАЯ ФЕДЕРАЦИЯ ПО ВОЕННО – ПРИКЛАДНЫМ ВИДАМ СПОРТА» объединяет лучших атлетов страны, развивает физическую культуру и боевую готовность.',
    quote_title: 'Здесь рождается сила, характер и честь.',
    quote_text: 'Военно-прикладные виды спорта — путь к мастерству, ответственности и служению Родине.',
    contact_phone: '+7 701 088 8808',
    contact_email: 'info@militarysport.kz',
    contact_address: 'г. Алматы, Республика Казахстан',
  },
  en: {
    hero_title: "WARRIOR'S WAY",
    hero_subtitle: '"NATIONAL FEDERATION OF MILITARY-APPLIED SPORTS" Public Organization',
    about_title: 'About Federation',
    about_text: '"NATIONAL FEDERATION OF MILITARY-APPLIED SPORTS" Public Organization unites top national athletes, advancing physical culture and combat readiness.',
    quote_title: 'Here strength, character, and honor are born.',
    quote_text: 'Military-applied sports are the path to mastery, responsibility, and service to the Motherland.',
    contact_phone: '+7 701 088 8808',
    contact_email: 'info@militarysport.kz',
    contact_address: 'Shymkent city, Republic of Kazakhstan',
  },
};

function loadTexts(lang: 'kk' | 'ru' | 'en'): Record<string, string> {
  try {
    const raw = localStorage.getItem(`admin_texts_${lang}`);
    if (!raw) return DEFAULT_TEXTS[lang];
    return { ...DEFAULT_TEXTS[lang], ...JSON.parse(raw) };
  } catch {
    return DEFAULT_TEXTS[lang];
  }
}

export default function TextEditor() {
  const [selectedLang, setSelectedLang] = useState<'kk' | 'ru' | 'en'>('kk');
  const [textsKk, setTextsKk] = useState<Record<string, string>>(() => loadTexts('kk'));
  const [textsRu, setTextsRu] = useState<Record<string, string>>(() => loadTexts('ru'));
  const [textsEn, setTextsEn] = useState<Record<string, string>>(() => loadTexts('en'));
  const [saved, setSaved] = useState(false);

  const currentTexts = selectedLang === 'kk' ? textsKk : selectedLang === 'ru' ? textsRu : textsEn;

  const update = (key: string, value: string) => {
    if (selectedLang === 'kk') {
      setTextsKk((prev) => ({ ...prev, [key]: value }));
    } else if (selectedLang === 'ru') {
      setTextsRu((prev) => ({ ...prev, [key]: value }));
    } else {
      setTextsEn((prev) => ({ ...prev, [key]: value }));
    }
  };

  const saveAll = () => {
    localStorage.setItem('admin_texts_kk', JSON.stringify(textsKk));
    localStorage.setItem('admin_texts_ru', JSON.stringify(textsRu));
    localStorage.setItem('admin_texts_en', JSON.stringify(textsEn));

    // Update live i18n bundle
    i18n.addResourceBundle('kk', 'translation', { admin_custom: textsKk }, true, true);
    i18n.addResourceBundle('ru', 'translation', { admin_custom: textsRu }, true, true);
    i18n.addResourceBundle('en', 'translation', { admin_custom: textsEn }, true, true);

    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="bg-background-900/60 rounded-xl border border-background-700/40 overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-6 py-4 border-b border-background-800/40">
        <div>
          <h3 className="font-heading text-foreground-200 text-sm tracking-widest uppercase">
            Сайт мәтіндерін редакциялау / Website Text Editor
          </h3>
          <p className="text-xs text-foreground-600 font-label mt-0.5">
            Казахский (KK), Русский (RU) и Английский (EN)
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Language Selector */}
          <div className="flex items-center bg-background-950 p-1 rounded-lg border border-background-700/50">
            <button
              onClick={() => setSelectedLang('kk')}
              className={`px-3 py-1 text-xs font-label uppercase rounded transition-colors cursor-pointer ${
                selectedLang === 'kk' ? 'bg-accent-500 text-background-950 font-semibold' : 'text-foreground-400'
              }`}
            >
              Қазақша (KK)
            </button>
            <button
              onClick={() => setSelectedLang('ru')}
              className={`px-3 py-1 text-xs font-label uppercase rounded transition-colors cursor-pointer ${
                selectedLang === 'ru' ? 'bg-accent-500 text-background-950 font-semibold' : 'text-foreground-400'
              }`}
            >
              Русский (RU)
            </button>
            <button
              onClick={() => setSelectedLang('en')}
              className={`px-3 py-1 text-xs font-label uppercase rounded transition-colors cursor-pointer ${
                selectedLang === 'en' ? 'bg-accent-500 text-background-950 font-semibold' : 'text-foreground-400'
              }`}
            >
              English (EN)
            </button>
          </div>

          {saved && (
            <span className="text-xs text-green-400 font-label tracking-wide">
              <i className="ri-check-line mr-1" />Saved
            </span>
          )}

          <button
            onClick={saveAll}
            className="px-4 py-1.5 bg-accent-500 hover:bg-accent-600 text-background-950 text-xs font-label tracking-widest uppercase rounded cursor-pointer whitespace-nowrap transition-colors"
          >
            Сохранить
          </button>
        </div>
      </div>

      <div className="p-6 space-y-4 max-h-[550px] overflow-y-auto">
        <div className="bg-background-950/60 p-3 rounded-lg border border-background-800 text-xs text-accent-400 font-label flex items-center gap-2">
          <i className="ri-translate-2 text-sm" />
          <span>Editing text for language: <strong>{selectedLang === 'kk' ? 'Қазақша' : selectedLang === 'ru' ? 'Русский' : 'English'}</strong></span>
        </div>

        {TEXT_FIELDS.map((field) => (
          <div key={field.key}>
            <label className="block text-xs text-foreground-500 font-label tracking-wide mb-1.5 uppercase">
              {field.label}
            </label>
            {field.multiline ? (
              <textarea
                value={currentTexts[field.key] || ''}
                onChange={(e) => update(field.key, e.target.value)}
                rows={3}
                className="w-full bg-background-900 border border-background-700 rounded px-3 py-2 text-sm text-foreground-300 focus:outline-none focus:border-accent-500 transition-colors resize-none"
              />
            ) : (
              <input
                type="text"
                value={currentTexts[field.key] || ''}
                onChange={(e) => update(field.key, e.target.value)}
                className="w-full bg-background-900 border border-background-700 rounded px-3 py-2 text-sm text-foreground-300 focus:outline-none focus:border-accent-500 transition-colors"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}